import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { ProductMedia } from "./ProductCard";
import { SPEC_NOTE } from "../data/products";
import { EASE } from "./Reveal";

export default function ProductModal({ product, onClose }) {
  useEffect(() => {
    if (!product) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    if (window.__lenis) window.__lenis.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [product, onClose]);

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8"
          data-testid="product-detail-modal"
        >
          <div className="absolute inset-0 bg-ink/45 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={product.name}
            initial={{ opacity: 0, y: 44, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="relative max-h-[88vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-navy-900/12 bg-white shadow-[0_40px_120px_rgba(5,11,24,0.8)]"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close product details"
              data-testid="product-modal-close"
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-navy-900/15 bg-[#F3F7FB]/70 text-slate-600 backdrop-blur transition hover:border-brand-emeraldLight hover:text-brand-emeraldDark"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="grid md:grid-cols-2">
              <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[380px]">
                <ProductMedia product={product} large />
              </div>
              <div className="flex flex-col p-6 sm:p-8">
                <span className="chip">{product.comingSoon ? "Upcoming" : product.category}</span>
                <h3 className="mt-4 font-display text-2xl font-bold uppercase leading-tight tracking-wide text-ink sm:text-3xl">
                  {product.name}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">{product.description}</p>

                <div className="mt-5 rounded-xl border border-navy-900/[0.08] bg-navy-900/[0.03] p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-blueDark">Application</p>
                  <p className="mt-1.5 text-sm text-slate-500">{product.application}</p>
                </div>

                <p className="mt-4 text-xs leading-relaxed text-slate-500">{SPEC_NOTE}</p>

                <div className="mt-auto flex flex-wrap gap-3 pt-6">
                  <Link
                    to={`/contact?product=${encodeURIComponent(product.name)}`}
                    onClick={onClose}
                    data-testid="product-modal-enquire-button"
                    className="btn btn-primary"
                  >
                    Enquire About This Product
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to={`/products/${product.id}`}
                    onClick={onClose}
                    className="btn btn-outline"
                    data-testid="product-modal-full-page-link"
                  >
                    Full Page
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
